import { Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUser } from '@src/shared/contexts/UserContext'
import { useFunctionApps, useSetTimerEnabled } from '../../api/function-apps-api'
import { dataGridSx } from '../../styles/dataGridSx'

interface DisabledTimerRow {
  id: string
  appId: string
  appName: string
  subscriptionName: string
  functionName: string
  schedule: string
}

const DisabledFunctionsPage = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [filter, setFilter] = useState('')
  const [pendingId, setPendingId] = useState<string | null>(null)

  const [apps, isLoading] = useFunctionApps()
  const [setTimerEnabled, isToggling] = useSetTimerEnabled()

  const canToggle = !!user?.rights.executeAdminCommand

  const rows = useMemo<DisabledTimerRow[]>(
    () =>
      apps.flatMap((app) =>
        app.timerTriggers
          .filter((timer) => timer.isDisabled)
          .map((timer) => ({
            id: `${app.id}::${timer.name}`,
            appId: app.id,
            appName: app.name,
            subscriptionName: app.subscriptionName,
            functionName: timer.name,
            schedule: timer.schedule,
          })),
      ),
    [apps],
  )

  const filteredRows = useMemo(() => {
    const term = filter.toLowerCase()
    return rows.filter(
      (row) => row.functionName.toLowerCase().includes(term) || row.appName.toLowerCase().includes(term),
    )
  }, [rows, filter])

  const handleEnable = async (row: DisabledTimerRow) => {
    setPendingId(row.id)
    try {
      await setTimerEnabled({ appId: row.appId, functionName: row.functionName, enabled: true })
    } finally {
      setPendingId(null)
    }
  }

  const columns: GridColDef<DisabledTimerRow>[] = [
    {
      field: 'appName',
      headerName: t('command.functionApp', 'Function app'),
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={500}>
            {row.appName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.subscriptionName}
          </Typography>
        </Box>
      ),
    },
    { field: 'functionName', headerName: t('command.function', 'Function'), flex: 1 },
    {
      field: 'schedule',
      headerName: t('command.schedule', 'Schedule'),
      flex: 1,
      renderCell: ({ value }) => <code style={{ fontSize: 12 }}>{value}</code>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: ({ row }) =>
        canToggle ? (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={isToggling && pendingId === row.id}
            startIcon={isToggling && pendingId === row.id ? <CircularProgress size={16} /> : undefined}
            onClick={() => handleEnable(row)}
          >
            {t('command.toggle.enable', 'Enable')}
          </Button>
        ) : null,
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', px: 2 }}>
      <Box sx={{ py: 1 }}>
        <TextField
          size="small"
          placeholder={t('command.filter', 'Filter...')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 220 }}
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <DataGrid
          sx={dataGridSx}
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          localeText={{ noRowsLabel: t('command.disabled.empty', 'No disabled functions') }}
        />
      </Box>
    </Box>
  )
}

export default DisabledFunctionsPage
